'use client'

import { useState } from 'react'

import { range } from 'lodash'
import {
  Circle,
  CircleDashed,
  EllipsisVertical,
  Eye,
  LinkIcon,
  PlugIcon,
} from 'lucide-react'
import Image from 'next/image'

import { Card } from '~/components/Card'
import { InfiniteScroll } from '~/components/InfiniteScroll'
import {
  EmptyState,
  NetworkLogo,
  Skeleton,
  Timestamp,
  Tooltip,
} from '~/components/component-library'
import { Dialog } from '~/components/component-library/Dialog'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from '~/components/component-library/Dropdown'
import {
  ApproveMessageButton,
  ApproveMessageDialogContent,
  MessageDetailDialogContent,
  SubmitMessageButton,
} from '~/components/smart-accounts/MessageDialog'
import { YouHaveSignedIcon } from '~/components/smart-accounts/utils'
import {
  useMessageProperties,
  useMessages,
} from '~/hooks/smart-accounts/useMessages'
import { useSmartAccount } from '~/hooks/useSmartAccounts'
import { OrgMessage } from '~/types'
import { EvmAddress } from '~/utils/address'
import { copyToClipboard } from '~/utils/copyToClipboard'
import { cn, INTERACTIVE_ROW_HOVER_BG } from '~/utils/tailwind-utils'

export const MessageFeedWrapper = ({
  smartAccountId,
  smartAccountAddress,
}: {
  smartAccountId: string
  smartAccountAddress: EvmAddress.Type
}) => {
  const { smartAccount } = useSmartAccount(smartAccountAddress)
  const { messages } = useMessages({ smartAccountId })

  // No reason to show messages for an operating account right now
  if (smartAccount?.threshold === 1 || messages.length === 0) return <></>

  return (
    <Card grow={false}>
      <MessageFeed smartAccountId={smartAccountId} />
    </Card>
  )
}

const MessageFeed = ({ smartAccountId }: { smartAccountId: string }) => {
  const [selectedMessage, setSelectedMessage] = useState<OrgMessage>()
  const { smartAccount } = useSmartAccount(selectedMessage?.smartAccountAddress)
  const { messages, hasMore, isInitialLoading, fetchNextPage } = useMessages({
    smartAccountId,
  })

  const onOpenChange = (open: boolean, message: OrgMessage) => {
    setSelectedMessage(open ? message : undefined)
  }

  const onClickMessage = (message: OrgMessage) => {
    setSelectedMessage(message)
  }

  if (messages.length === 0) {
    return <></>
  }

  return (
    <div className="flex flex-col space-y-6">
      <h3>Messages</h3>
      {isInitialLoading ? (
        <div className="space-y-4">
          {range(10).map((i) => (
            <div key={i} className="flex justify-between p-2">
              <div className="flex flex-grow space-x-2">
                <Skeleton length={2} />
                <Skeleton length={16} />
              </div>
              <Skeleton length={2} />
            </div>
          ))}
        </div>
      ) : messages.length === 0 ? (
        <EmptyState>There are no messages</EmptyState>
      ) : (
        <InfiniteScroll
          hasNextPage={hasMore}
          isNextPageLoading={isInitialLoading}
          list={messages}
          loadNextPage={async () => {
            const { data } = await fetchNextPage()
            return data?.pages.flat()
          }}
          renderItem={(message) => (
            <Message
              message={message}
              onClick={() => onClickMessage(message)}
            />
          )}
          itemHeight={80}
        />
      )}
      {selectedMessage && (
        <Dialog
          open={!!selectedMessage}
          onOpenChange={(open) => onOpenChange(open, selectedMessage)}
        >
          {smartAccount &&
          smartAccount.threshold <= selectedMessage.signers.length ? (
            <MessageDetailDialogContent
              messageId={selectedMessage.id}
              onClose={() => onOpenChange(false, selectedMessage)}
            />
          ) : (
            <ApproveMessageDialogContent
              messageId={selectedMessage.id}
              onClose={() => onOpenChange(false, selectedMessage)}
            />
          )}
        </Dialog>
      )}
    </div>
  )
}

const Message = ({
  message,
  onClick,
}: {
  message: OrgMessage
  onClick?: () => void
}) => {
  const { smartAccount } = useSmartAccount(message.smartAccountAddress)
  const { canSign, canSubmit, showSignature } = useMessageProperties(message.id)

  return (
    <div
      className={cn(
        'flex w-full cursor-pointer justify-between rounded-lg p-2',
        INTERACTIVE_ROW_HOVER_BG,
      )}
      onClick={onClick}
    >
      <div className="flex w-full min-w-0 shrink flex-grow items-center space-x-6 text-sm">
        <MessageIcon message={message} networkLogo />
        <div className="space-y-1 overflow-hidden">
          <div className="truncate">{message.name}</div>
          <div className="flex items-center space-x-1.5 text-secondary">
            <div className="flex-shrink-0">
              <div className="line-clamp-1">
                <Timestamp
                  timestamp={message.createdAt}
                  type="relative"
                  abbreviate
                />
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="flex shrink-0 items-center space-x-2">
        {canSign && <ApproveMessageButton messageId={message.id} />}
        {canSubmit && <SubmitMessageButton messageId={message.id} />}
        {showSignature && smartAccount && (
          <YouHaveSignedIcon
            numMissingSignatures={
              smartAccount.threshold - message.signers.length
            }
          />
        )}
        <DropdownMenu>
          <DropdownMenuTrigger>
            <EllipsisVertical className="mx-1 my-2 h-4 w-4" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className={`w-48`}>
            <DropdownMenuItem onClick={onClick}>
              <Eye />
              <div>View details</div>
            </DropdownMenuItem>
            <DropdownMenuItem
              onClick={() => {
                const url = `${window.location.origin}/accounts/${message.smartAccountAddress}/messages/${message.id}/`
                copyToClipboard(url, 'Link copied to clipboard')
              }}
            >
              <LinkIcon />
              <div>Copy link</div>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  )
}

const MessageIcon = ({
  message,
  size = 'sm',
  networkLogo,
}: {
  message: OrgMessage
  size?: 'sm' | 'lg'
  networkLogo?: boolean
}) => {
  const { smartAccount } = useSmartAccount(message.smartAccountAddress)
  const iconSize = size === 'sm' ? 24 : 56
  const className = cn('rounded-lg', {
    'w-8 h-8': size === 'sm',
    'w-14 h-14': size === 'lg',
  })

  const isApproved =
    smartAccount && smartAccount.threshold <= message.signers.length

  const isWalletConnectMessage =
    message.walletConnectAppName ||
    message.walletConnectAppIcon ||
    message.walletConnectSessionRequestId ||
    message.walletConnectSessionRequestTopic

  return (
    <div className="relative rounded-lg bg-gray-100 dark:bg-gray-700">
      <div className={cn(className, 'flex items-center p-1')}>
        {isWalletConnectMessage ? (
          <Image
            src={
              message.walletConnectAppIcon ?? '/wallets/walletconnect-logo.svg'
            }
            alt={message.walletConnectAppName ?? 'wallet connect app'}
            width={iconSize}
            height={iconSize}
          />
        ) : (
          <PlugIcon size={iconSize} />
        )}
      </div>
      {networkLogo ? (
        <div className="absolute -bottom-1 -right-1">
          <NetworkLogo size={'md'} chainIds={[message.chainId]} />
        </div>
      ) : (
        <MessageStatusIcon
          isApproved={!!isApproved}
          className="absolute -bottom-1 -right-1"
          tooltip={false}
        />
      )}
    </div>
  )
}

const MessageStatusIcon = ({
  isApproved,
  className,
  tooltip = true,
}: {
  isApproved: boolean
  className?: string
  tooltip?: boolean
}) => {
  const icon = (
    <div
      className={cn({
        'text-yellow-500': !isApproved,
        'text-green-500': isApproved,
      })}
    >
      {isApproved ? (
        <Circle width={20} className={className} />
      ) : (
        <CircleDashed width={20} className={className} />
      )}
    </div>
  )

  if (tooltip)
    return (
      <div>
        <Tooltip content={isApproved ? 'Approved' : 'Pending approval'}>
          {icon}
        </Tooltip>
      </div>
    )

  return icon
}
